import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    X, Plus, GripVertical, ChevronDown, ChevronUp, Loader2,
    Eye, Dumbbell, Zap, Trash2,
} from "lucide-react";
import {
    RM_OPTIONS, STRENGTH_EQUIPMENT_BAR, STRENGTH_EQUIPMENT_OTHER,
    FLY_INS, STANCES, MOVEMENT_PATTERNS, NAMING_TOKENS, DEFAULT_NAMING_ORDER,
} from "@/lib/exerciseTaxonomy";
import { MAX_TYPE_META, MAX_TYPE_OPTIONS } from "@/lib/maxUnits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red";

// =====================================================================
// CreateExercisesDialog — Paladin-style cartesian-batch creator
// =====================================================================
export default function CreateExercisesDialog({ onClose, onCreated }) {
    const [discipline, setDiscipline] = useState("strength");
    const [exerciseNames, setExerciseNames] = useState([""]);
    const [videoUrl, setVideoUrl] = useState("");
    const [thumbnailUrl, setThumbnailUrl] = useState("");

    // Strength selections
    const [rms, setRms] = useState([]);
    const [velocitiesTpV, setVelocitiesTpV] = useState([]);
    const [velocitiesPV, setVelocitiesPV] = useState([]);
    const [velocitiesAV, setVelocitiesAV] = useState([]);
    const [equipBars, setEquipBars] = useState([]);
    const [equipOthers, setEquipOthers] = useState([]);

    // Speed/Power selections
    const [flyIns, setFlyIns] = useState([]);
    const [stances, setStances] = useState([]);

    // Shared
    const [movementPattern, setMovementPattern] = useState("");
    const [maxesConfig, setMaxesConfig] = useState([{ type: "weight_lbs" }]);
    const [orderOfNaming, setOrderOfNaming] = useState(DEFAULT_NAMING_ORDER.strength);

    const [customOptions, setCustomOptions] = useState({}); // {option_key: [values]}
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    // Load saved custom options once
    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/custom-options`, { withCredentials: true });
                const grouped = {};
                for (const row of r.data || []) {
                    grouped[row.option_key] = grouped[row.option_key] || [];
                    grouped[row.option_key].push(row.value);
                }
                setCustomOptions(grouped);
            } catch (_e) { /* non-fatal */ }
        })();
    }, []);

    // Reset selections + name order when discipline changes
    useEffect(() => {
        setMovementPattern("");
        setOrderOfNaming(DEFAULT_NAMING_ORDER[discipline]);
    }, [discipline]);

    const isStrength = discipline === "strength";
    const isSpeedPower = discipline === "speed_power";

    // Persist a + Custom value so it shows up forever
    const persistCustom = async (option_key, value) => {
        try {
            await axios.post(
                `${API}/coach/custom-options`,
                { option_key, value },
                { withCredentials: true },
            );
            setCustomOptions((c) => {
                const existing = c[option_key] || [];
                const lower = value.toLowerCase();
                if (existing.some((v) => v.toLowerCase() === lower)) return c; // already there (case-insensitive)
                return { ...c, [option_key]: [...existing, value] };
            });
        } catch (e) {
            // Surface the failure so coaches know their value didn't save —
            // previously we swallowed the error and the value vanished.
            const detail = e?.response?.data?.detail || e?.message || "Save failed";
            toast.error(`Could not save "${value}"`, { description: detail });
        }
    };

    // Merge the 3 metric lists into one tagged array for the existing
    // naming/preview/submit pipeline. Values are formatted "metric value"
    // (e.g. "TpV 300ms", "pV 1.5m/s") so generated exercise names stay
    // unambiguous when a coach configures multiple metric kinds.
    const velocities = useMemo(() => {
        const out = [];
        for (const v of velocitiesTpV) out.push(`TpV ${v}`);
        for (const v of velocitiesPV) out.push(`pV ${v}`);
        for (const v of velocitiesAV) out.push(`aV ${v}`);
        return out;
    }, [velocitiesTpV, velocitiesPV, velocitiesAV]);

    // Live preview names — first 12
    const previewNames = useMemo(() => {
        const names = exerciseNames.map((n) => n.trim()).filter(Boolean);
        if (!names.length || !movementPattern) return [];
        const out = [];
        const ctx = (name, opts = {}) => ({
            name,
            movement_pattern: movementPattern,
            ...opts,
        });
        const build = (c) => {
            const tokenVal = {
                "Exercise Name": c.name,
                "RM": c.rm,
                "Equipment": [c.equipBar, c.equipOther].filter(Boolean).join(" "),
                "Velocity": c.velocity,
                "Fly In": c.fly_in,
                "Stance": c.stance,
                "Category": (c.movement_pattern || "").toLowerCase(),
            };
            return orderOfNaming
                .map((t) => tokenVal[t])
                .filter((v) => v && String(v).trim())
                .join(" ");
        };
        const seen = new Set();
        const push = (c) => {
            const n = build(c);
            if (n && !seen.has(n)) { seen.add(n); out.push(n); }
        };
        if (isStrength) {
            const rmsArr = rms.length ? rms : [null];
            const barsArr = equipBars.length ? equipBars : [null];
            const otherArr = equipOthers.length ? equipOthers : [null];
            const velArr = velocities.length ? velocities : [null];
            for (const n of names) {
                for (const rm of rmsArr) for (const bar of barsArr)
                    for (const other of otherArr) for (const vel of velArr) {
                        push(ctx(n, { rm, equipBar: bar, equipOther: other, velocity: vel }));
                    }
            }
        } else {
            const flyArr = flyIns.length ? flyIns : [null];
            const stArr = stances.length ? stances : [null];
            for (const n of names) {
                for (const fly of flyArr) for (const st of stArr) {
                    push(ctx(n, { fly_in: fly, stance: st }));
                }
            }
        }
        return out;
    }, [exerciseNames, rms, equipBars, equipOthers, velocities, flyIns, stances, movementPattern, orderOfNaming, isStrength]);

    const canCreate = previewNames.length > 0;

    const submit = async () => {
        if (!canCreate) {
            toast.error("Add at least one name, pick a Category, and configure naming tokens.");
            return;
        }
        setSaving(true);
        try {
            const body = {
                discipline,
                exercise_names: exerciseNames.map((n) => n.trim()).filter(Boolean),
                movement_pattern: movementPattern,
                rm_options: isStrength ? rms : [],
                velocity_params: isStrength ? velocities : [],
                equipment_bar_options: isStrength ? equipBars : [],
                equipment_other_options: isStrength ? equipOthers : [],
                fly_in_options: isSpeedPower ? flyIns : [],
                stance_options: isSpeedPower ? stances : [],
                maxes_config: maxesConfig.map((m) => ({ type: m.type, label: m.label || null })),
                video_url: videoUrl || null,
                thumbnail_url: thumbnailUrl || null,
                order_of_naming: orderOfNaming,
            };
            const r = await axios.post(`${API}/coach/exercises/bulk-create`, body, { withCredentials: true });
            const created = r.data?.created ?? 0;
            const skipped = r.data?.skipped_count ?? 0;
            if (created === 0 && skipped > 0) {
                // Owner spec 2026-02-17 — no dups ever. Show the dup names so
                // the coach can see exactly what collided.
                const sampleNames = (r.data.skipped_existing || []).slice(0, 3).join(", ");
                toast.info("Nothing new created — names already exist", {
                    description: `${skipped} duplicate name${skipped === 1 ? "" : "s"} skipped${sampleNames ? `: ${sampleNames}${skipped > 3 ? "…" : ""}` : ""}`,
                });
            } else if (created === 0) {
                toast.info("Nothing new created — generator produced 0 exercises.");
            } else if (skipped > 0) {
                toast.success(`Created ${created} exercise${created === 1 ? "" : "s"} · ${skipped} duplicate name${skipped === 1 ? "" : "s"} skipped`);
            } else {
                toast.success(`Created ${created} exercise${created === 1 ? "" : "s"}`);
            }
            onCreated?.();
        } catch (e) {
            toast.error("Couldn't create", { description: e?.response?.data?.detail || "Server error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-end md:items-center justify-center p-0 md:p-6 overflow-y-auto">
            <div
                data-testid="create-exercises-dialog"
                className="bg-zinc-950 border border-zinc-800 w-full md:max-w-3xl my-6 max-h-[92vh] flex flex-col rounded-none md:rounded-lg"
            >
                <div className="h-1 bg-brand-red shrink-0" />
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900 shrink-0">
                    <div className="font-display text-2xl tracking-wider text-white uppercase">Create Exercises</div>
                    <button type="button" onClick={onClose} className="text-zinc-300 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 md:p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
                    {/* Discipline tabs */}
                    <div className="grid grid-cols-2 gap-2 bg-black/40 border border-zinc-900 rounded-md p-1.5">
                        <TabButton
                            active={isStrength}
                            onClick={() => setDiscipline("strength")}
                            icon={<Dumbbell className="w-4 h-4" />}
                            label="Strength"
                            color="brand-red"
                            testId="discipline-strength"
                        />
                        <TabButton
                            active={isSpeedPower}
                            onClick={() => setDiscipline("speed_power")}
                            icon={<Zap className="w-4 h-4" />}
                            label="Speed/Power"
                            color="lime"
                            testId="discipline-speed-power"
                        />
                    </div>

                    {/* Exercise Name(s) */}
                    <Section title="Exercise Name(s)" testId="section-names" trailing={
                        <button
                            type="button"
                            data-testid="add-exercise-name"
                            onClick={() => setExerciseNames([...exerciseNames, ""])}
                            className={`text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded font-semibold ${isStrength ? "bg-brand-red text-white" : "bg-lime-400 text-black"}`}
                        ><Plus className="w-3.5 h-3.5 inline -mt-0.5" /> Add Exercise</button>
                    }>
                        <div className="space-y-2">
                            {exerciseNames.map((n, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        placeholder={isStrength ? "e.g., Box Squat" : "e.g., Sprint Start"}
                                        value={n}
                                        data-testid={`exercise-name-${i}`}
                                        onChange={(e) => {
                                            const cp = [...exerciseNames];
                                            cp[i] = e.target.value;
                                            setExerciseNames(cp);
                                        }}
                                        className={inputCls}
                                    />
                                    {exerciseNames.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => setExerciseNames(exerciseNames.filter((_, idx) => idx !== i))}
                                            className="text-zinc-400 hover:text-brand-red p-2"
                                        ><Trash2 className="w-4 h-4" /></button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Strength-only sections */}
                    {isStrength && (
                        <>
                            <PillSection
                                title="RMs"
                                options={RM_OPTIONS}
                                customs={customOptions["rm"] || []}
                                selected={rms}
                                onChange={setRms}
                                onCustomAdd={(v) => persistCustom("rm", v)}
                                color="brand-red"
                                testId="rms"
                            />
                            <div data-testid="velocity-metric-sections" className="space-y-4 border border-zinc-900 rounded-lg p-4 bg-zinc-950/40">
                                <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-300">Accel/Velocity Parameters</div>
                                <p className="text-[11px] text-zinc-500 -mt-2">
                                    Add specific velocity targets per metric. Hit <strong>+ Add</strong> to save a custom value (e.g. <em>300ms</em> for TpV, <em>1.5m/s</em> for pV).
                                </p>
                                <PillSection
                                    title="TpV (Time to Peak Velocity)"
                                    options={[]}
                                    customs={customOptions["velocity_tpv"] || []}
                                    selected={velocitiesTpV}
                                    onChange={setVelocitiesTpV}
                                    onCustomAdd={(v) => persistCustom("velocity_tpv", v)}
                                    customPlaceholder="e.g. 300ms"
                                    addLabel="Add TpV"
                                    color="brand-red"
                                    testId="velocities-tpv"
                                />
                                <PillSection
                                    title="pV (Peak Velocity)"
                                    options={[]}
                                    customs={customOptions["velocity_pv"] || []}
                                    selected={velocitiesPV}
                                    onChange={setVelocitiesPV}
                                    onCustomAdd={(v) => persistCustom("velocity_pv", v)}
                                    customPlaceholder="e.g. 1.5m/s"
                                    addLabel="Add pV"
                                    color="brand-red"
                                    testId="velocities-pv"
                                />
                                <PillSection
                                    title="aV (Average Velocity)"
                                    options={[]}
                                    customs={customOptions["velocity_av"] || []}
                                    selected={velocitiesAV}
                                    onChange={setVelocitiesAV}
                                    onCustomAdd={(v) => persistCustom("velocity_av", v)}
                                    customPlaceholder="e.g. 0.8m/s"
                                    addLabel="Add aV"
                                    color="brand-red"
                                    testId="velocities-av"
                                />
                            </div>
                            <PillSection
                                title="Bar Equipment"
                                options={STRENGTH_EQUIPMENT_BAR}
                                customs={customOptions["equipment_bar"] || []}
                                selected={equipBars}
                                onChange={setEquipBars}
                                onCustomAdd={(v) => persistCustom("equipment_bar", v)}
                                color="brand-red"
                                testId="equipment-bars"
                            />
                            <PillSection
                                title="Other Equipment"
                                options={STRENGTH_EQUIPMENT_OTHER}
                                customs={customOptions["equipment_other"] || []}
                                selected={equipOthers}
                                onChange={setEquipOthers}
                                onCustomAdd={(v) => persistCustom("equipment_other", v)}
                                color="brand-red"
                                testId="equipment-others"
                            />
                        </>
                    )}

                    {/* Speed/Power-only sections */}
                    {isSpeedPower && (
                        <>
                            <PillSection
                                title="Fly Ins"
                                options={FLY_INS}
                                customs={customOptions["fly_in"] || []}
                                selected={flyIns}
                                onChange={setFlyIns}
                                onCustomAdd={(v) => persistCustom("fly_in", v)}
                                color="lime"
                                testId="fly-ins"
                            />
                            <PillSection
                                title="Stance"
                                options={STANCES}
                                customs={customOptions["stance"] || []}
                                selected={stances}
                                onChange={setStances}
                                onCustomAdd={(v) => persistCustom("stance", v)}
                                color="lime"
                                testId="stances"
                            />
                        </>
                    )}

                    {/* Number of Maxes (shared) */}
                    <Section title={`Number of Maxes (${maxesConfig.length}/5)`} testId="section-maxes" trailing={
                        <button
                            type="button"
                            data-testid="add-max-row"
                            disabled={maxesConfig.length >= 5}
                            onClick={() => setMaxesConfig([...maxesConfig, { type: "weight_lbs" }])}
                            className={`text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded font-semibold disabled:opacity-30 ${isStrength ? "bg-brand-red text-white" : "bg-lime-400 text-black"}`}
                        ><Plus className="w-3.5 h-3.5 inline -mt-0.5" /> Add Max</button>
                    }>
                        <div className="space-y-2">
                            {maxesConfig.map((m, i) => (
                                <div key={i} className="flex items-center gap-2" data-testid={`max-row-${i}`}>
                                    <span className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 w-6">#{i + 1}</span>
                                    <select
                                        value={m.type}
                                        data-testid={`max-type-${i}`}
                                        onChange={(e) => setMaxesConfig(maxesConfig.map((mm, idx) => idx === i ? { ...mm, type: e.target.value } : mm))}
                                        className={`${inputCls} flex-1`}
                                    >
                                        {MAX_TYPE_OPTIONS.map((t) => (
                                            <option key={t} value={t}>{MAX_TYPE_META[t].dropdownLabel}</option>
                                        ))}
                                    </select>
                                    {maxesConfig.length > 1 && (
                                        <button
                                            type="button"
                                            data-testid={`remove-max-${i}`}
                                            onClick={() => setMaxesConfig(maxesConfig.filter((_, idx) => idx !== i))}
                                            className="text-zinc-400 hover:text-brand-red p-2"
                                        ><X className="w-4 h-4" /></button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Video URL (shared) */}
                    <Section title="Video URL" testId="section-video">
                        <input
                            type="url"
                            placeholder="https://youtube.com/..."
                            value={videoUrl}
                            data-testid="video-url"
                            onChange={(e) => setVideoUrl(e.target.value)}
                            className={inputCls}
                        />
                        <input
                            type="url"
                            placeholder="Thumbnail image URL (optional)"
                            value={thumbnailUrl}
                            data-testid="thumbnail-url"
                            onChange={(e) => setThumbnailUrl(e.target.value)}
                            className={`${inputCls} mt-2`}
                        />
                    </Section>

                    {/* Category — movement pattern, single select */}
                    <Section title={isStrength ? "Category (Movement Pattern)" : "Category"} testId="section-category">
                        <div className="flex flex-wrap gap-2">
                            {[...MOVEMENT_PATTERNS[discipline], ...(customOptions[`movement_pattern_${discipline}`] || [])].map((p) => (
                                <Pill
                                    key={p}
                                    label={p}
                                    active={movementPattern === p}
                                    onClick={() => setMovementPattern(movementPattern === p ? "" : p)}
                                    color={isStrength ? "brand-red" : "lime"}
                                    testId={`movement-${p.toLowerCase().replace(/\s+/g, "-")}`}
                                />
                            ))}
                            <AddCustomPill
                                onAdd={async (v) => {
                                    await persistCustom(`movement_pattern_${discipline}`, v);
                                    setMovementPattern(v);
                                }}
                                color={isStrength ? "brand-red" : "lime"}
                            />
                        </div>
                    </Section>

                    {/* Order of Naming */}
                    <Section
                        title="Order of Naming"
                        testId="section-naming"
                        trailing={
                            <div className="inline-flex items-center gap-2 bg-black/40 border border-zinc-800 rounded px-2 py-1">
                                <button
                                    type="button"
                                    data-testid="naming-count-down"
                                    onClick={() => setOrderOfNaming(orderOfNaming.slice(0, -1))}
                                    disabled={orderOfNaming.length <= 1}
                                    className="text-zinc-300 disabled:opacity-30 hover:text-white"
                                ><ChevronDown className="w-4 h-4" /></button>
                                <span className="text-white text-sm tabular-nums w-4 text-center">{orderOfNaming.length}</span>
                                <button
                                    type="button"
                                    data-testid="naming-count-up"
                                    disabled={orderOfNaming.length >= NAMING_TOKENS[discipline].length}
                                    onClick={() => {
                                        const remaining = NAMING_TOKENS[discipline].filter((t) => !orderOfNaming.includes(t));
                                        if (remaining.length) setOrderOfNaming([...orderOfNaming, remaining[0]]);
                                    }}
                                    className="text-zinc-300 disabled:opacity-30 hover:text-white"
                                ><ChevronUp className="w-4 h-4" /></button>
                            </div>
                        }
                    >
                        <p className="text-xs text-zinc-300 mb-3">Select the order of how exercise names will be generated</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {orderOfNaming.map((tok, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <span className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 w-5">#{i + 1}</span>
                                    <select
                                        value={tok}
                                        data-testid={`naming-token-${i}`}
                                        onChange={(e) => {
                                            const cp = [...orderOfNaming];
                                            cp[i] = e.target.value;
                                            setOrderOfNaming(cp);
                                        }}
                                        className={`${inputCls} flex-1`}
                                    >
                                        {NAMING_TOKENS[discipline].map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        </div>
                        {/* Example output */}
                        <div className="mt-3 px-4 py-3 border border-zinc-800 bg-black/40 rounded">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-1">Example output</div>
                            <div data-testid="example-output" className={`text-sm font-mono ${isStrength ? "text-brand-red" : "text-lime-400"}`}>
                                {previewNames[0] || <span className="text-zinc-500">— Pick a Category and at least one name to preview —</span>}
                            </div>
                        </div>
                    </Section>

                    {/* Preview list */}
                    {showPreview && (
                        <div data-testid="preview-list" className="border border-zinc-800 bg-black/40 rounded p-4">
                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-2">
                                Will create {previewNames.length} exercise{previewNames.length === 1 ? "" : "s"}
                            </div>
                            <ul className="space-y-1 max-h-60 overflow-y-auto">
                                {previewNames.map((n, i) => (
                                    <li key={i} className="text-sm text-white font-mono">{i + 1}. {n}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-zinc-900 flex items-center justify-end gap-2 shrink-0 bg-zinc-950">
                    <button type="button" onClick={onClose} className="btn-ghost-tactical" data-testid="cancel-create">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowPreview((v) => !v)}
                        data-testid="toggle-preview"
                        className="text-[11px] tracking-[0.15em] uppercase px-3 py-2 border border-zinc-800 text-zinc-200 hover:text-white inline-flex items-center gap-1.5"
                    >
                        <Eye className="w-3.5 h-3.5" /> {showPreview ? "Hide" : "Preview"} ({previewNames.length})
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving || !canCreate}
                        data-testid="submit-create"
                        className={`text-[11px] tracking-[0.15em] uppercase px-4 py-2 font-semibold rounded disabled:opacity-40 inline-flex items-center gap-1.5 ${isStrength ? "bg-brand-red text-white" : "bg-lime-400 text-black"}`}
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Create {previewNames.length || ""} Exercise{previewNames.length === 1 ? "" : "s"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// =====================================================================
// Subcomponents
// =====================================================================

function TabButton({ active, onClick, icon, label, color, testId }) {
    const activeCls = color === "lime"
        ? "bg-lime-400 text-black"
        : "bg-brand-red text-white";
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`flex items-center justify-center gap-2 py-2.5 rounded text-sm font-semibold tracking-wide ${active ? activeCls : "text-zinc-300 hover:text-white"}`}
        >
            {icon} {label}
        </button>
    );
}

function Section({ title, children, trailing, testId }) {
    const [open, setOpen] = useState(true);
    return (
        <div data-testid={testId} className="border border-zinc-800 bg-black/40 rounded">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-zinc-900">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="flex items-center gap-2 text-zinc-200 hover:text-white"
                >
                    <GripVertical className="w-3.5 h-3.5 text-zinc-500" />
                    {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    <span className="text-sm font-semibold tracking-wide text-white">{title}</span>
                </button>
                {trailing}
            </div>
            {open && <div className="px-4 py-3">{children}</div>}
        </div>
    );
}

function PillSection({ title, options, customs, selected, onChange, onCustomAdd, color, testId, customPlaceholder, addLabel }) {
    return (
        <Section title={title} testId={`section-${testId}`}>
            <div className="flex flex-wrap gap-2">
                {[...options, ...(customs || [])].map((opt) => (
                    <Pill
                        key={opt}
                        label={opt}
                        active={selected.includes(opt)}
                        onClick={() => onChange(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt])}
                        color={color}
                        testId={`${testId}-${opt.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "_")}`}
                    />
                ))}
                <AddCustomPill
                    onAdd={async (v) => { await onCustomAdd?.(v); onChange([...selected, v]); }}
                    color={color}
                    placeholder={customPlaceholder}
                    label={addLabel}
                    testId={testId}
                />
            </div>
        </Section>
    );
}

function Pill({ label, active, onClick, color, testId }) {
    const activeCls = color === "lime" ? "bg-lime-400 text-black border-lime-400" : "bg-brand-red text-white border-brand-red";
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`text-xs px-3.5 py-2 border rounded font-medium transition-colors ${active ? activeCls : "border-zinc-700 text-zinc-200 hover:border-zinc-500 hover:text-white"}`}
        >
            {label}
        </button>
    );
}

function AddCustomPill({ onAdd, color, placeholder, label, testId }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState("");
    if (!editing) {
        return (
            <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid={testId ? `${testId}-add-custom` : "add-custom-pill"}
                className={`text-xs px-3.5 py-2 border-2 border-dashed rounded font-medium ${color === "lime" ? "border-lime-400/40 text-lime-300 hover:border-lime-400" : "border-zinc-700 text-zinc-300 hover:border-brand-red hover:text-brand-red"}`}
            >
                <Plus className="w-3 h-3 inline -mt-0.5" /> {label || "Add"}
            </button>
        );
    }
    const commit = () => {
        const v = value.trim();
        if (v) onAdd?.(v);
        setValue("");
        setEditing(false);
    };
    return (
        <div className="inline-flex items-center gap-1">
            <input
                autoFocus
                value={value}
                data-testid={testId ? `${testId}-custom-input` : "custom-pill-input"}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { setValue(""); setEditing(false); } }}
                onBlur={commit}
                placeholder={placeholder || "Type then Enter…"}
                className="text-xs px-3 py-2 bg-black border border-zinc-700 text-white rounded focus:outline-none focus:border-brand-red"
            />
        </div>
    );
}
